/**
 * Cosmetic items: what they are, what they cost, and how they look.
 *
 * Shared by client and server on purpose — the server prices every purchase
 * from this file so a tampered client cannot buy a 600-coin celebration for
 * nothing, and the client renders from the same definitions so the shop and
 * the pitch can never disagree about what an item actually does.
 *
 * Cosmetics never touch the rules. Nothing in here changes a legal move, a
 * save zone or a clock — a paying player must not have a better game, only a
 * better looking one.
 */

import { defaultConfig } from "../core/config.js";

export type CosmeticKind = "celebration" | "gimmick";

/** Where an item comes from. Free items are owned by everyone from day one. */
export type CosmeticSource = "free" | "shop" | "achievement";

export interface CosmeticBase {
  id: string;
  kind: CosmeticKind;
  /** English name; doubles as the i18n key, like everything else in the UI. */
  name: string;
  description: string;
  source: CosmeticSource;
  /** Coin price. Absent for free and achievement items. */
  price?: number;
  /** Achievement that unlocks this item, for source === "achievement". */
  unlockedBy?: { achievementId: string; tier: number };
}

/**
 * How a goal celebration throws its particles. Every value is read by
 * useGoalCelebration; nothing here is decorative metadata.
 */
export interface CelebrationStyle {
  particleCount: number;
  /** Total arc the particles fan across, centred on straight up, in degrees. */
  spreadDeg: number;
  /** Travel distance range in px. */
  distance: [number, number];
  /** How far each particle sags at the end of its flight, in px. */
  drop: [number, number];
  /** Rotation range in degrees. */
  spin: [number, number];
  /** Edge length range in px. */
  size: [number, number];
  /** Cycled per particle. Empty means "use the scoring team's colour". */
  colors: string[];
  /** Seconds the burst takes; the overlay clears itself afterwards. */
  durationSec: number;
  /** Optional extra class on the overlay for one-off flourishes. */
  flourish?: "rings" | "shockwave" | "rain";
}

export interface CelebrationItem extends CosmeticBase {
  kind: "celebration";
  style: CelebrationStyle;
}

/**
 * A small ornament worn by a player token.
 *
 * Paths live in a 24×24 box whose BOTTOM edge rests just above the token's
 * middle, so everything is drawn in the lower half of the box (roughly y 13
 * to 23) and sits on the player's head. Nothing may reach down over the role
 * letter — the letter is information, the ornament is decoration, and
 * decoration never wins.
 */
export interface GimmickItem extends CosmeticBase {
  kind: "gimmick";
  /** SVG path data drawn above the token. Empty for "no gimmick". */
  paths: string[];
  /** Fill colour; "team" means the token's own colour. */
  fill: string | "team";
  /** Fine vertical nudge inside the box; negative sits higher. */
  offsetY: number;
}

export type CosmeticItem = CelebrationItem | GimmickItem;

export const CELEBRATIONS: CelebrationItem[] = [
  {
    id: "celebration.classic",
    kind: "celebration",
    name: "Classic",
    description: "The confetti burst every goal has always had.",
    source: "free",
    style: {
      particleCount: 14,
      spreadDeg: 300,
      distance: [90, 180],
      drop: [40, 110],
      spin: [-260, 320],
      size: [18, 22],
      colors: [],
      durationSec: 1.5,
    },
  },
  {
    id: "celebration.fireworks",
    kind: "celebration",
    name: "Fireworks",
    description: "A wide, slow burst that keeps climbing before it falls.",
    source: "shop",
    price: 450,
    style: {
      particleCount: 26,
      spreadDeg: 360,
      distance: [140, 260],
      drop: [-30, 40],
      spin: [-420, 420],
      size: [8, 14],
      colors: ["#ffd166", "#ef476f", "#06d6a0", "#4f8ff5", "#ffffff"],
      durationSec: 2.1,
      flourish: "rings",
    },
  },
  {
    id: "celebration.ticker-tape",
    kind: "celebration",
    name: "Ticker Tape",
    description: "Long ribbons drifting down over the pitch, cup-final style.",
    source: "shop",
    price: 400,
    style: {
      particleCount: 30,
      spreadDeg: 120,
      distance: [40, 90],
      drop: [180, 320],
      spin: [-90, 90],
      size: [6, 30],
      colors: ["#f4f7f5", "#cfe0d5", "#9ac2ff", "#ffb0ae"],
      durationSec: 2.6,
      flourish: "rain",
    },
  },
  {
    id: "celebration.smoke-bomb",
    kind: "celebration",
    name: "Smoke Bomb",
    description: "A heavy low cloud that rolls outwards and hangs there.",
    source: "shop",
    price: 500,
    style: {
      particleCount: 18,
      spreadDeg: 200,
      distance: [70, 150],
      drop: [-10, 30],
      spin: [-40, 40],
      size: [34, 56],
      colors: ["#7fd6a3", "#4d9c74", "#e8f3ec"],
      durationSec: 2.4,
      flourish: "shockwave",
    },
  },
  {
    id: "celebration.golden-hour",
    kind: "celebration",
    name: "Golden Hour",
    description: "Gold sparks and a slow shockwave. Reach ten online wins.",
    source: "achievement",
    unlockedBy: { achievementId: "winner", tier: 2 },
    style: {
      particleCount: 22,
      spreadDeg: 340,
      distance: [110, 220],
      drop: [10, 70],
      spin: [-300, 300],
      size: [10, 18],
      colors: ["#f4c95d", "#ffe6a8", "#d9a327", "#fff8e2"],
      durationSec: 2.2,
      flourish: "shockwave",
    },
  },
];

export const GIMMICKS: GimmickItem[] = [
  {
    id: "gimmick.none",
    kind: "gimmick",
    name: "No gimmick",
    description: "Plain kit, nothing on top.",
    source: "free",
    paths: [],
    fill: "team",
    offsetY: 0,
  },
  {
    id: "gimmick.headband",
    kind: "gimmick",
    name: "Headband",
    description: "A thin band across the top of every player.",
    source: "shop",
    price: 150,
    paths: ["M3 16 h18 v3.4 H3 z", "M19 15.4 l3.6 -1.8 v5 l-3.6 -1.8 z"],
    fill: "#f4f7f5",
    offsetY: 0,
  },
  {
    id: "gimmick.cap",
    kind: "gimmick",
    name: "Cap",
    description: "A little peaked cap, worn at a slight angle.",
    source: "shop",
    price: 220,
    paths: ["M5 18.5 a7 5.5 0 0 1 14 0 z", "M1.5 18.2 h9.5 v2.4 H1.5 z"],
    fill: "team",
    offsetY: 0,
  },
  {
    id: "gimmick.shades",
    kind: "gimmick",
    name: "Shades",
    description: "Sunglasses. For players who are simply better than you.",
    source: "shop",
    price: 250,
    paths: ["M3 15 h7 v4 a3.5 3.5 0 0 1 -7 0 z", "M14 15 h7 v4 a3.5 3.5 0 0 1 -7 0 z", "M10 15.6 h4 v1.6 h-4 z"],
    fill: "#141a16",
    offsetY: 0,
  },
  {
    id: "gimmick.captain",
    kind: "gimmick",
    name: "Captain's Badge",
    description: "A small captain's mark above every one of your players.",
    source: "shop",
    price: 200,
    // One path with three subpaths: drawn even-odd, the inner two become
    // cut-outs so the shirt colour shows through as the badge markings.
    paths: ["M15 13.2 h7.6 v7.6 h-7.6 z M16.5 15.2 h4.6 v1.5 h-4.6 z M16.5 17.7 h3 v1.5 h-3 z"],
    fill: "#f4c95d",
    offsetY: 0,
  },
  {
    id: "gimmick.crown",
    kind: "gimmick",
    name: "Crown",
    description: "Worn only by Rasenschach Cup champions.",
    source: "achievement",
    unlockedBy: { achievementId: "cup-run", tier: 4 },
    paths: ["M4 20 l2.6 -6.5 l5.4 4.2 l5.4 -4.2 l2.6 6.5 z", "M4 20 h16 v2.4 H4 z"],
    fill: "#f4c95d",
    offsetY: 0,
  },
];

export const COSMETICS: CosmeticItem[] = [...CELEBRATIONS, ...GIMMICKS];

const BY_ID = new Map<string, CosmeticItem>(COSMETICS.map((item) => [item.id, item]));

export function cosmeticById(id: string): CosmeticItem | undefined {
  return BY_ID.get(id);
}

/** What a player wears before they own anything. */
export const DEFAULT_EQUIPPED: Record<CosmeticKind, string> = {
  celebration: "celebration.classic",
  gimmick: "gimmick.none",
};

export function celebrationStyle(id: string | null | undefined): CelebrationStyle {
  const item = id ? BY_ID.get(id) : undefined;
  if (item && item.kind === "celebration") return item.style;
  return (BY_ID.get(DEFAULT_EQUIPPED.celebration) as CelebrationItem).style;
}

export function gimmick(id: string | null | undefined): GimmickItem {
  const item = id ? BY_ID.get(id) : undefined;
  if (item && item.kind === "gimmick") return item;
  return BY_ID.get(DEFAULT_EQUIPPED.gimmick) as GimmickItem;
}

/**
 * The squad slots a gimmick can be pinned to.
 *
 * A gimmick belongs to one player, not to the whole team, and the player is
 * chosen once in the shop rather than per match. A piece id carries its team
 * ("A-striker-4"), which changes from game to game, so what is stored is the
 * team-agnostic tail — the slot. Resolving it back is `${team}-${slot}`.
 *
 * Derived from the standard line-up so the two can never drift apart. A board
 * with a different roster (puzzles, the tutorial) simply finds no match and
 * shows no ornament, which is the right answer there anyway.
 */
export interface SquadSlot {
  /** Tail of the piece id, e.g. "striker-4". */
  id: string;
  role: "goalkeeper" | "defender" | "midfielder" | "striker";
  /** 1-based position within the role, for labels like "Striker 2". */
  ordinal: number;
}

export const SQUAD_SLOTS: SquadSlot[] = (() => {
  const seen = new Map<string, number>();
  return defaultConfig.teamSetup.teamA.map((def, index) => {
    const ordinal = (seen.get(def.type) ?? 0) + 1;
    seen.set(def.type, ordinal);
    return { id: `${def.type}-${index}`, role: def.type, ordinal };
  });
})();

const SLOT_IDS = new Set(SQUAD_SLOTS.map((slot) => slot.id));

export function isSquadSlot(id: string | null | undefined): boolean {
  return typeof id === "string" && SLOT_IDS.has(id);
}

/** Where a gimmick sits until the player moves it. The lone midfielder. */
export const DEFAULT_GIMMICK_SLOT = SQUAD_SLOTS.find((s) => s.role === "midfielder")?.id
  ?? SQUAD_SLOTS[0]!.id;

/** Everyone owns these without spending or earning anything. */
export const FREE_COSMETIC_IDS: string[] = COSMETICS.filter((c) => c.source === "free").map((c) => c.id);

/**
 * Coin bundles. Prices are deliberately not micro-amounts: a payment provider
 * charges a fixed fee per transaction, so a €2 sale loses about a quarter of
 * its value to processing while a €10 sale loses under a tenth. Bundles are
 * what make small cosmetic prices possible at all.
 */
export interface CoinBundle {
  id: string;
  coins: number;
  /** Minor units (cents). The provider holds the authoritative price. */
  priceCents: number;
  currency: "EUR";
  /** Extra coins on top of the base rate, for display ("+15% extra"). */
  bonusPercent: number;
}

export const COIN_BUNDLES: CoinBundle[] = [
  { id: "coins.small", coins: 600, priceCents: 499, currency: "EUR", bonusPercent: 0 },
  { id: "coins.medium", coins: 1400, priceCents: 999, currency: "EUR", bonusPercent: 17 },
  { id: "coins.large", coins: 3000, priceCents: 1999, currency: "EUR", bonusPercent: 25 },
];

const BUNDLE_BY_ID = new Map(COIN_BUNDLES.map((b) => [b.id, b]));

export function coinBundleById(id: string): CoinBundle | undefined {
  return BUNDLE_BY_ID.get(id);
}

/** Coins awarded for playing, so the shop is not the only way in. */
export const COIN_REWARDS = {
  /** A welcome purse, so a new account can afford something at once. */
  signup: 150,
  /** Winning a rated online match. */
  onlineWin: 25,
  /** A draw still pays, at a lower rate. */
  onlineDraw: 10,
  /**
   * Base rate for reaching a badge tier. The amount actually paid is computed
   * on the server from the tier and the badge's own threshold, and is never
   * sent to the client: a published payout table would turn the badge list
   * into a price list and reward grinding the cheapest one.
   */
  achievementTier: 60,
} as const;
